/**
 * A QR encoder, hand-rolled, because the house rule is no third-party deps and
 * a join URL is a bounded problem.
 *
 * Deliberately narrow, and the narrowing is what keeps it small enough to trust:
 *
 *  - **Byte mode only.** A URL is not alphanumeric-mode material (lowercase and
 *    `/` are outside that charset), so supporting one mode costs nothing.
 *  - **Error correction level L, versions 1–5.** Every one of those is a SINGLE
 *    error-correction block, so the whole block-interleaving apparatus — the
 *    fiddliest part of the spec and the easiest to get subtly wrong — does not
 *    exist here. Capacity is 106 bytes, and a join URL is around forty. Level L
 *    is right for a symbol on a screen: L's ~7% recovery exists for print damage
 *    and dirt, neither of which happens to a phone held across a table.
 *  - **No version information block.** That is only required from version 7 up,
 *    which the version cap puts out of reach.
 *
 * `encodeQr` returns null rather than throwing when the text does not fit. The
 * caller falls back to the join code in large type, which is a perfectly good
 * way to get a six-character code across a table.
 *
 * Validated end to end by feeding the output of versions 2–5 through the `jsQR`
 * decoder outside this repo (no dependency is added here) and checking the
 * decoded text round-trips. The in-repo tests check the spec-level invariants —
 * syndromes, format BCH, function patterns — because those localise a fault,
 * while a decoder only says yes or no.
 */

export interface QrSymbol {
  /** Modules per side, excluding the quiet zone. */
  size: number;
  /** Row-major, one byte per module: 1 = dark. */
  modules: Uint8Array;
  version: number;
}

/** Level L, versions 1–5: data codewords, EC codewords, alignment centres. */
const VERSIONS = [
  { version: 1, data: 19, ec: 7, align: [] as number[] },
  { version: 2, data: 34, ec: 10, align: [6, 18] },
  { version: 3, data: 55, ec: 15, align: [6, 22] },
  { version: 4, data: 80, ec: 20, align: [6, 26] },
  { version: 5, data: 108, ec: 26, align: [6, 30] },
] as const;

/** Level L is `01` in the two format-info EC bits. */
const EC_LEVEL_BITS = 0b01;
const FORMAT_GENERATOR = 0b10100110111;
const FORMAT_MASK = 0b101010000010010;

// ---------------------------------------------------------------------------
// GF(256), primitive polynomial 0x11d — the field the whole spec is written in
// ---------------------------------------------------------------------------

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);

{
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255]!;
}

export function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[LOG[a]! + LOG[b]!]!;
}

/** α^i, exported so a test can check the codeword's syndromes are all zero. */
export function gfExp(i: number): number {
  return EXP[((i % 255) + 255) % 255]!;
}

/** Product of (x - α^i) for i < degree. Coefficients highest power first. */
export function generatorPoly(degree: number): number[] {
  let poly = [1];
  for (let i = 0; i < degree; i++) {
    const next = new Array<number>(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j++) {
      const c = poly[j]!;
      next[j] = (next[j] ?? 0) ^ c;
      next[j + 1] = (next[j + 1] ?? 0) ^ gfMul(c, EXP[i]!);
    }
    poly = next;
  }
  return poly;
}

/** Reed–Solomon check codewords: the remainder of data(x)·x^ec mod g(x). */
export function ecCodewords(data: readonly number[], ecLen: number): number[] {
  const gen = generatorPoly(ecLen);
  const res = [...data, ...new Array<number>(ecLen).fill(0)];
  for (let i = 0; i < data.length; i++) {
    const coef = res[i]!;
    if (coef === 0) continue;
    for (let j = 0; j < gen.length; j++) res[i + j] = (res[i + j] ?? 0) ^ gfMul(gen[j]!, coef);
  }
  return res.slice(data.length);
}

// ---------------------------------------------------------------------------
// Format information — 5 data bits, 10 BCH bits, XOR'd with a fixed mask so an
// all-zero format never produces an all-light region.
// ---------------------------------------------------------------------------

export function formatBits(mask: number): number {
  const data = (EC_LEVEL_BITS << 3) | mask;
  let rem = data << 10;
  for (let i = 14; i >= 10; i--) {
    if (rem & (1 << i)) rem ^= FORMAT_GENERATOR << (i - 10);
  }
  return ((data << 10) | rem) ^ FORMAT_MASK;
}

// ---------------------------------------------------------------------------
// Bit stream
// ---------------------------------------------------------------------------

function toBits(bytes: Uint8Array, capacityBytes: number): number[] {
  const bits: number[] = [];
  const push = (value: number, width: number) => {
    for (let i = width - 1; i >= 0; i--) bits.push((value >> i) & 1);
  };

  push(0b0100, 4); // byte mode
  push(bytes.length, 8); // versions 1–9 use an 8-bit character count
  for (const b of bytes) push(b, 8);

  const capacityBits = capacityBytes * 8;
  // Terminator: up to four zeroes, fewer if the symbol is nearly full.
  for (let i = 0; i < 4 && bits.length < capacityBits; i++) bits.push(0);
  while (bits.length % 8 !== 0) bits.push(0);

  // Pad alternately with the two bytes the spec names, until full.
  const pad = [0xec, 0x11];
  let p = 0;
  while (bits.length < capacityBits) {
    push(pad[p % 2]!, 8);
    p++;
  }
  return bits;
}

function bitsToCodewords(bits: readonly number[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = (byte << 1) | (bits[i + j] ?? 0);
    out.push(byte);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Matrix
// ---------------------------------------------------------------------------

export interface Grid {
  size: number;
  /** 1 = dark, 0 = light. */
  cells: Uint8Array;
  /** Function patterns and format areas: data must not be written here. */
  fixed: Uint8Array;
}

function makeGrid(size: number): Grid {
  return { size, cells: new Uint8Array(size * size), fixed: new Uint8Array(size * size) };
}

function set(g: Grid, row: number, col: number, dark: boolean, fixed = true): void {
  const i = row * g.size + col;
  g.cells[i] = dark ? 1 : 0;
  if (fixed) g.fixed[i] = 1;
}

function at(g: Grid, row: number, col: number): number {
  if (row < 0 || col < 0 || row >= g.size || col >= g.size) return 0;
  return g.cells[row * g.size + col]!;
}

function placeFinder(g: Grid, row: number, col: number): void {
  // The 7x7 eye plus its one-module separator, clipped at the symbol edge.
  for (let r = -1; r <= 7; r++) {
    for (let c = -1; c <= 7; c++) {
      const rr = row + r;
      const cc = col + c;
      if (rr < 0 || cc < 0 || rr >= g.size || cc >= g.size) continue;
      const ring = r >= 0 && r <= 6 && c >= 0 && c <= 6;
      const outer = ring && (r === 0 || r === 6 || c === 0 || c === 6);
      const core = r >= 2 && r <= 4 && c >= 2 && c <= 4;
      set(g, rr, cc, outer || core);
    }
  }
}

function placeAlignment(g: Grid, row: number, col: number): void {
  for (let r = -2; r <= 2; r++) {
    for (let c = -2; c <= 2; c++) {
      const edge = Math.max(Math.abs(r), Math.abs(c));
      set(g, row + r, col + c, edge !== 1);
    }
  }
}

function placeFunctionPatterns(g: Grid, version: number): void {
  const { size } = g;

  placeFinder(g, 0, 0);
  placeFinder(g, 0, size - 7);
  placeFinder(g, size - 7, 0);

  for (let i = 8; i < size - 8; i++) {
    const dark = i % 2 === 0;
    set(g, 6, i, dark);
    set(g, i, 6, dark);
  }

  const centres = VERSIONS[version - 1]!.align;
  for (const r of centres) {
    for (const c of centres) {
      // The three corners are already finders; alignment never overlaps them.
      const nearFinder =
        (r <= 8 && c <= 8) || (r <= 8 && c >= size - 9) || (r >= size - 9 && c <= 8);
      if (nearFinder) continue;
      placeAlignment(g, r, c);
    }
  }

  // Reserve the format-information strips so data placement skips them.
  for (let i = 0; i <= 8; i++) {
    if (i !== 6) {
      set(g, 8, i, false);
      set(g, i, 8, false);
    }
  }
  for (let i = 0; i < 8; i++) set(g, 8, size - 1 - i, false);
  for (let i = 0; i < 7; i++) set(g, size - 1 - i, 8, false);

  // The one module that is always dark, in every symbol ever printed.
  set(g, size - 8, 8, true);
}

function placeData(g: Grid, bits: readonly number[]): void {
  const { size } = g;
  let index = 0;
  let upward = true;

  for (let right = size - 1; right > 0; right -= 2) {
    // Column 6 is the vertical timing pattern; the zigzag steps over it.
    if (right === 6) right = 5;
    for (let vert = 0; vert < size; vert++) {
      const row = upward ? size - 1 - vert : vert;
      for (const col of [right, right - 1]) {
        if (g.fixed[row * size + col]) continue;
        set(g, row, col, (bits[index++] ?? 0) === 1, false);
      }
    }
    upward = !upward;
  }
}

const MASKS: ((r: number, c: number) => boolean)[] = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (_r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

function applyMask(g: Grid, mask: number): void {
  const fn = MASKS[mask]!;
  for (let r = 0; r < g.size; r++) {
    for (let c = 0; c < g.size; c++) {
      const i = r * g.size + c;
      if (g.fixed[i]) continue;
      if (fn(r, c)) g.cells[i] = (g.cells[i] ?? 0) ^ 1;
    }
  }
}

/** The spec's four penalty rules. Lower is a symbol a camera finds faster. */
export function penalty(g: Grid): number {
  const { size } = g;
  let score = 0;

  // Rule 1: runs of five or more identical modules.
  for (let a = 0; a < size; a++) {
    for (const horizontal of [true, false]) {
      let run = 1;
      let prev = horizontal ? at(g, a, 0) : at(g, 0, a);
      for (let b = 1; b < size; b++) {
        const cur = horizontal ? at(g, a, b) : at(g, b, a);
        if (cur === prev) {
          run++;
        } else {
          if (run >= 5) score += 3 + (run - 5);
          run = 1;
          prev = cur;
        }
      }
      if (run >= 5) score += 3 + (run - 5);
    }
  }

  // Rule 2: 2x2 blocks of one colour.
  for (let r = 0; r < size - 1; r++) {
    for (let c = 0; c < size - 1; c++) {
      const v = at(g, r, c);
      if (v === at(g, r, c + 1) && v === at(g, r + 1, c) && v === at(g, r + 1, c + 1)) score += 3;
    }
  }

  // Rule 3: the finder-lookalike sequence, either way round.
  const A = [1, 0, 1, 1, 1, 0, 1, 0, 0, 0, 0];
  const B = [0, 0, 0, 0, 1, 0, 1, 1, 1, 0, 1];
  for (let a = 0; a < size; a++) {
    for (let b = 0; b <= size - 11; b++) {
      let mA = true;
      let mB = true;
      let nA = true;
      let nB = true;
      for (let k = 0; k < 11; k++) {
        const h = at(g, a, b + k);
        const v = at(g, b + k, a);
        if (h !== A[k]) mA = false;
        if (h !== B[k]) mB = false;
        if (v !== A[k]) nA = false;
        if (v !== B[k]) nB = false;
      }
      if (mA) score += 40;
      if (mB) score += 40;
      if (nA) score += 40;
      if (nB) score += 40;
    }
  }

  // Rule 4: drift away from an even split of dark and light.
  let dark = 0;
  for (let i = 0; i < g.cells.length; i++) dark += g.cells[i]!;
  const ratio = (dark * 100) / (size * size);
  score += Math.floor(Math.abs(ratio - 50) / 5) * 10;

  return score;
}

function placeFormat(g: Grid, mask: number): void {
  const bits = formatBits(mask);
  const bit = (i: number) => ((bits >> i) & 1) === 1;
  const { size } = g;

  for (let i = 0; i <= 5; i++) set(g, i, 8, bit(i));
  set(g, 7, 8, bit(6));
  set(g, 8, 8, bit(7));
  set(g, 8, 7, bit(8));
  for (let i = 9; i < 15; i++) set(g, 8, 14 - i, bit(i));

  for (let i = 0; i < 8; i++) set(g, 8, size - 1 - i, bit(i));
  for (let i = 8; i < 15; i++) set(g, size - 15 + i, 8, bit(i));

  set(g, size - 8, 8, true);
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

/** Byte capacity at each supported version, for callers that want to check. */
export function qrCapacity(version: number): number {
  const spec = VERSIONS[version - 1];
  return spec ? spec.data - 2 : 0; // mode nibble + 8-bit count = two codewords
}

export function encodeQr(text: string): QrSymbol | null {
  const bytes = new TextEncoder().encode(text);
  const spec = VERSIONS.find((v) => bytes.length <= v.data - 2);
  if (!spec) return null;

  const data = bitsToCodewords(toBits(bytes, spec.data));
  const codewords = [...data, ...ecCodewords(data, spec.ec)];

  const bits: number[] = [];
  for (const cw of codewords) for (let i = 7; i >= 0; i--) bits.push((cw >> i) & 1);

  const size = spec.version * 4 + 17;
  let best: Grid | null = null;
  let bestScore = Infinity;

  for (let mask = 0; mask < 8; mask++) {
    const g = makeGrid(size);
    placeFunctionPatterns(g, spec.version);
    placeData(g, bits);
    applyMask(g, mask);
    placeFormat(g, mask);
    const score = penalty(g);
    if (score < bestScore) {
      bestScore = score;
      best = g;
    }
  }

  if (!best) return null;
  return { size, modules: best.cells, version: spec.version };
}
